// eslint-disable-next-line import/no-cycle
import {
  ComponentData, ComponentMetadata, ErrorConfigForm, PersistenceList,
} from '@/types/automated-scenarios';
import { i18n } from '@/i18n';
// eslint-disable-next-line import/no-cycle
import {
  AutomatedScenarioState as state,
  getOperatorChildByOutput,
  isRootOfTree,
} from '@/composables/AutomatedScenarios';
// eslint-disable-next-line import/no-cycle
import { TypeCampaignEnum } from '@/types';
import { computed } from 'vue';
import {
  helpers, maxLength, minLength, required,
} from '@vuelidate/validators';
import useVuelidate from '@vuelidate/core';
// eslint-disable-next-line import/no-cycle
import { getComponentMetadata } from '@/components/automated-scenarios/metadata';

export interface DeclencheurTriggerInternalEventData {
  custom_title: string;
  event: string | null;
  webhook: number;
  customPersistance?: PersistenceList;
  [key: string]: any; // Fields of subcomponents
}

let translation: any;
(async () => {
  translation = await i18n;
})();

const DeclencheurTriggerInternalEventMetadata: ComponentMetadata<DeclencheurTriggerInternalEventData> = {
  Meta: {
    kind: 'declencheur',
    id: 'boxtriggerinternalevent',
    label: 'automatedScenarios.triggers.triggerInternalEvent.label',
    icon: 'fa-arrow-right-to-bracket',
    hasConfiguration: true,
    incompatibilities: {
      automation: {
        next_operators: [
          'boxtriggerinternalevent', 'boxsitepasstime', 'boxquitsite',
          'boxperiodiclaunching', 'boxscrollpage', 'boxstatuscommand',
          'boxcustomjs', 'registeronsite', 'subscribetonewsletter',
          'boxsendemail', 'boxsendsms', 'boxdisplayfreepopup',
          'boxdisplayfreemodel', 'boxsendfbmessenger', 'boxsendpushnotification',
          'boxfiltre_2', 'openemail', 'clicklink',
          'boxvisitpage', 'boxtriggerinternalevent', 'boxsendrcs', 'boxsendwhatsapp',
        ],
      },
    },
    component: 'DeclencheurTriggerInternalEvent',
    availableInCampaign: [TypeCampaignEnum.AUTOMATION],
  },
  Create(data: DeclencheurTriggerInternalEventData = {
    custom_title: translation.global.t('automatedScenarios.triggers.triggerInternalEvent.label'),
    event: null,
    webhook: 0,
  }): ComponentData<DeclencheurTriggerInternalEventData> {
    const returnValue: ComponentData<DeclencheurTriggerInternalEventData> = {
      logo_box: 'fa-arrow-right-to-bracket',
      ...data,
      id: 'boxtriggerinternalevent',
      form_name: 'boxtriggerinternalevent',
    };

    if (data.customPersistance && Object.keys(data.customPersistance).length > 0) {
      returnValue.customPersistance = data.customPersistance;
    }

    return returnValue;
  },
  async Validate(data: DeclencheurTriggerInternalEventData): Promise<ErrorConfigForm> {
    const rules = computed(() => {
      let rulesToValidate = {
        custom_title: {
          required,
          minLength: minLength(3),
          maxLength: maxLength(45),
        },
        event: {
          required: helpers.withMessage(translation.global.t('vuelidate.required'), required),
        },
      };

      // If the selected event include a subcomponent and subproperties, we add its form's validation's rules to the existing ones
      if ('subcomponent' in data && data.subcomponent) {
        const metaOfSubComponent = getComponentMetadata(data.subcomponent);

        if (metaOfSubComponent && 'validationRules' in metaOfSubComponent && typeof metaOfSubComponent.validationRules === 'function') {
          rulesToValidate = { ...rulesToValidate, ...metaOfSubComponent.validationRules(data) };
        }
      }

      return rulesToValidate;
    });

    const v$ = useVuelidate(rules, data);
    const success = await v$.value.$validate();
    return {
      success,
      validate: v$,
    };
  },
  Outputs(data: DeclencheurTriggerInternalEventData): number {
    return 1;
  },
  CanBeDeleted(operatorId: string): boolean|string {
    const isRoot = isRootOfTree(operatorId);

    if (isRoot) {
      const childOperator = getOperatorChildByOutput(operatorId, 'output_1');
      if (childOperator !== null && typeof (state.scenario.data.operators[childOperator]) !== undefined
        && state.scenario.data.operators[childOperator].properties.class !== 'declencheur') {
        return 'automatedScenarios.cant_remove_operator';
      }
    }

    return true;
  },
  Label(data: DeclencheurTriggerInternalEventData): string {
    return data.custom_title;
  },
};

export default DeclencheurTriggerInternalEventMetadata;
